import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Star, Send, Loader2, PartyPopper } from "lucide-react";
import { useCustomerRating } from "@/hooks/useCustomerRating";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";

interface TabletRatingScreenProps {
  restaurantId: string;
  tableNumber: string;
  orderId?: string;
}

const QUESTIONS = [
  { id: "food", label: "Qualidade da comida", emoji: "🍽️" },
  { id: "service", label: "Atendimento", emoji: "👨‍🍳" },
  { id: "environment", label: "Ambiente", emoji: "✨" },
  { id: "value", label: "Custo-benefício", emoji: "💰" },
  { id: "overall", label: "Experiência geral", emoji: "⭐" },
];

interface StarRatingProps {
  value: number;
  onChange: (value: number) => void;
}

function StarRating({ value, onChange }: StarRatingProps) {
  const [hover, setHover] = useState(0);

  return (
    <div className="flex gap-2">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(star)}
          className="transition-transform hover:scale-110"
        >
          <Star
            className={cn(
              "w-10 h-10 transition-colors",
              star <= (hover || value)
                ? "fill-amber-400 text-amber-400"
                : "text-slate-600"
            )}
          />
        </button>
      ))}
    </div>
  );
}

export function TabletRatingScreen({
  restaurantId,
  tableNumber,
  orderId,
}: TabletRatingScreenProps) {
  const { submitRating, isSubmitting } = useCustomerRating();
  const [submitted, setSubmitted] = useState(false);

  const [ratings, setRatings] = useState({
    food: 0,
    service: 0,
    environment: 0,
    value: 0,
    overall: 0,
  });

  const [comments, setComments] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [acceptMarketing, setAcceptMarketing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const success = await submitRating({
      restaurantId,
      tableNumber,
      orderId,
      foodQuality: ratings.food || undefined,
      serviceQuality: ratings.service || undefined,
      environment: ratings.environment || undefined,
      valueForMoney: ratings.value || undefined,
      overallExperience: ratings.overall || undefined,
      comments: comments || undefined,
      customerName: customerName || undefined,
      customerEmail: customerEmail || undefined,
      customerPhone: customerPhone || undefined,
      acceptMarketing,
    });

    if (success) {
      setSubmitted(true);
    }
  };

  const hasAnyRating = Object.values(ratings).some((r) => r > 0);

  // Success screen
  if (submitted) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", duration: 0.5 }}
        >
          <PartyPopper className="w-24 h-24 text-primary mb-6" />
        </motion.div>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-3xl font-bold text-white mb-4"
        >
          Obrigado pela sua avaliação!
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-slate-400 text-lg"
        >
          Sua opinião é muito importante para nós
        </motion.p>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto p-6">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-white mb-2">
            Como foi sua experiência?
          </h2>
          <p className="text-slate-400">
            Sua opinião nos ajuda a melhorar cada vez mais
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Rating questions */}
          <div className="space-y-6">
            {QUESTIONS.map((question) => (
              <div
                key={question.id}
                className="bg-slate-800/50 rounded-xl p-6 border border-slate-700"
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-2xl">{question.emoji}</span>
                  <span className="text-lg font-medium text-white">
                    {question.label}
                  </span>
                </div>
                <StarRating
                  value={ratings[question.id as keyof typeof ratings]}
                  onChange={(value) =>
                    setRatings((prev) => ({ ...prev, [question.id]: value }))
                  }
                />
              </div>
            ))}
          </div>

          {/* Comments */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-300">
              Deixe um comentário (opcional)
            </label>
            <Textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Conte-nos mais sobre sua experiência..."
              className="bg-slate-800/50 border-slate-700 text-white h-24 resize-none"
            />
          </div>

          {/* Contact info */}
          <div className="bg-slate-800/30 rounded-xl p-6 border border-slate-700/50 space-y-4">
            <h3 className="text-lg font-medium text-white mb-4">
              Quer receber novidades e promoções exclusivas?
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-300">Nome</label>
                <Input
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Seu nome"
                  className="bg-slate-800/50 border-slate-700 text-white"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-300">
                  E-mail
                </label>
                <Input
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="seu@email.com"
                  className="bg-slate-800/50 border-slate-700 text-white"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium text-slate-300">
                  WhatsApp
                </label>
                <Input
                  type="tel"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  placeholder="(00) 00000-0000"
                  className="bg-slate-800/50 border-slate-700 text-white"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Checkbox
                id="marketing"
                checked={acceptMarketing}
                onCheckedChange={(checked) =>
                  setAcceptMarketing(checked === true)
                }
              />
              <label
                htmlFor="marketing"
                className="text-sm text-slate-400 cursor-pointer"
              >
                Aceito receber novidades, promoções e ofertas exclusivas
              </label>
            </div>
          </div>

          {/* Submit */}
          <Button
            type="submit"
            size="lg"
            className="w-full h-14 text-lg font-semibold"
            disabled={!hasAnyRating || isSubmitting}
          >
            {isSubmitting ? (
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
            ) : (
              <Send className="w-5 h-5 mr-2" />
            )}
            Enviar Avaliação
          </Button>
        </form>
      </div>
    </div>
  );
}
